package com.nonopn.skydome.common.app.pages.probes

// Pure logic of the "Mes sondes" list: search, status filter, sort and counter.
// Kept free of any UI / model dependency so that it can be unit tested in isolation.

enum class ProbeStatusFilter { ALL, ACTIVE, INACTIVE }

enum class ProbeSort { NAME, RECENT, SITES }

/** Lightweight view of a probe: [siteNames] are the LPS Manager sites it covers. */
data class ProbeRow(
    val uuid: String,
    val name: String,
    val enabled: Boolean,
    val createdAtSecondsUtc: Long,
    val siteNames: List<String>,
)

data class ProbeListQuery(
    val search: String = "",
    val status: ProbeStatusFilter = ProbeStatusFilter.ALL,
    val sort: ProbeSort = ProbeSort.NAME,
) {
    /** True when the query is the untouched default (nothing to reset). */
    val isDefault: Boolean get() = this == ProbeListQuery()
}

// Base letter -> accented variants (kotlin common has no Normalizer).
private val ACCENT_GROUPS = mapOf(
    'a' to "àáâãäåāăą", 'c' to "çćč", 'd' to "ďđ", 'e' to "èéêëēĕėęě", 'g' to "ğ",
    'i' to "ìíîïĩīĭįı", 'l' to "ł", 'n' to "ñńň", 'o' to "òóôõöøōŏő", 'r' to "ŕř",
    's' to "śšş", 't' to "ť", 'u' to "ùúûüũūŭůűų", 'y' to "ýÿ", 'z' to "źżž",
)
private val ACCENT_MAP: Map<Char, Char> = ACCENT_GROUPS.flatMap { (base, accented) ->
    accented.map { it to base }
}.toMap()

/** Case and accent insensitive form of [value], used for every comparison of the search. */
fun normalizeForSearch(value: String): String {
    val sb = StringBuilder(value.length)
    value.trim().lowercase().forEach { c ->
        when (c) {
            'æ' -> sb.append("ae")
            'œ' -> sb.append("oe")
            'ß' -> sb.append("ss")
            else -> sb.append(ACCENT_MAP[c] ?: c)
        }
    }
    return sb.toString()
}

/**
 * True when every word of [query] (case and accent insensitive) is found in at least one of [fields].
 * A blank query matches everything. Only human readable fields must be given (names, addresses), never ids.
 */
fun matchesSearch(query: String, vararg fields: String?): Boolean {
    val words = normalizeForSearch(query).split(' ', '\t').filter { it.isNotEmpty() }
    if (words.isEmpty()) return true
    val haystacks = fields.mapNotNull { it }.map { normalizeForSearch(it) }
    return words.all { word -> haystacks.any { it.contains(word) } }
}

/** First covered site matching [search] when the probe name itself does not match (null otherwise). */
fun matchedSite(row: ProbeRow, search: String): String? {
    val needle = normalizeForSearch(search)
    if (needle.isEmpty() || normalizeForSearch(row.name).contains(needle)) return null
    return row.siteNames.firstOrNull { normalizeForSearch(it).contains(needle) }
}

private fun ProbeRow.matches(needle: String) = needle.isEmpty()
    || matchesSearch(needle, name, *siteNames.toTypedArray())

private fun ProbeRow.matches(status: ProbeStatusFilter) = when (status) {
    ProbeStatusFilter.ALL -> true
    ProbeStatusFilter.ACTIVE -> enabled
    ProbeStatusFilter.INACTIVE -> !enabled
}

/** Rows kept by [query], sorted. Ties are always broken by name then uuid so the order is stable. */
fun filterProbes(rows: List<ProbeRow>, query: ProbeListQuery): List<ProbeRow> {
    val needle = normalizeForSearch(query.search)
    val nameOrder = compareBy<ProbeRow>({ normalizeForSearch(it.name) }, { it.uuid })
    val comparator = when (query.sort) {
        ProbeSort.NAME -> nameOrder
        ProbeSort.RECENT -> compareByDescending<ProbeRow> { it.createdAtSecondsUtc }.then(nameOrder)
        ProbeSort.SITES -> compareByDescending<ProbeRow> { it.siteNames.size }.then(nameOrder)
    }
    return rows.filter { it.matches(query.status) && it.matches(needle) }.sortedWith(comparator)
}

/** Counter "N probes out of M". */
data class ProbeCount(val shown: Int, val total: Int)

fun countProbes(rows: List<ProbeRow>, query: ProbeListQuery) =
    ProbeCount(filterProbes(rows, query).size, rows.size)

/**
 * Query to use so that [selectedUuid] stays visible: the current one when it already shows the probe,
 * the default one (filters reset) otherwise.
 */
fun queryKeepingVisible(rows: List<ProbeRow>, query: ProbeListQuery, selectedUuid: String?): ProbeListQuery {
    if (selectedUuid == null || rows.none { it.uuid == selectedUuid }) return query
    return if (filterProbes(rows, query).any { it.uuid == selectedUuid }) query else ProbeListQuery()
}
